/**
 * Client socket du canal caisse ↔ téléphone-scanner (namespace /scango-caisse).
 *
 * ⚠️ POURQUOI CE SERVICE N'UTILISE PAS socketService (le socket global) :
 * la page téléphone (/scancaisse) est PUBLIQUE. La garde du routeur la laisse
 * passer sans appeler chektoken, donc socketService.initializeSocket() n'y est
 * jamais dispatché et socketService.socket y vaut null. Un téléphone qui en
 * dépendrait ne se connecterait jamais. Et s'il y parvenait, il rejoindrait le
 * namespace PAR DÉFAUT : un appareil public non authentifié dans le canal global
 * de l'application.
 *
 * Le voyant de l'accueil, lui, observe bien socketService - mais parce qu'il doit
 * refléter le socket réel pour ne pas mentir, pas par économie.
 *
 * Une instance par usage (la caisse et le téléphone en créent chacun une) : pas de
 * singleton, donc pas d'état résiduel d'un appairage à l'autre, et chaque instance
 * possède son socket (d'où le removeAllListeners sans danger dans deconnecter()).
 */

import { io } from "socket.io-client";

const NAMESPACE = "/scango-caisse";

/**
 * Construit l'URL de connexion à partir de VUE_APP_API_URL_SOCKET.
 *
 * ⚠️ PIÈGE : la variable vaut "http://localhost:9595/socket.io/". Passée telle
 * quelle à io(), la partie CHEMIN est interprétée comme un NAMESPACE - on se
 * connecterait à un namespace "/socket.io/" inexistant, et le serveur ne
 * répondrait jamais. Il faut séparer l'origine (l'hôte) du chemin (l'option
 * `path` de socket.io).
 */
const construireCible = () => {
    const u = new URL(process.env.VUE_APP_API_URL_SOCKET);
    return { url: u.origin + NAMESPACE, path: u.pathname };
};

/**
 * Un refus définitif exige un NOUVEL appairage ; une coupure se répare toute seule.
 * Confondre les deux laisserait le caissier attendre une reconnexion qui ne viendra
 * jamais.
 *
 * "Invalid namespace" en fait partie : le namespace n'est enregistré qu'au premier
 * appairage (cf. assurerNamespace côté serveur). L'obtenir signifie que le serveur
 * a redémarré - donc que le registre en mémoire est vide et que le lien est mort.
 */
const estRefusDefinitif = (code) =>
    code.indexOf("PAIR_") === 0
    || code.indexOf("ENROL_") === 0    // serveur resto : jeton révoqué/expiré/inconnu
    || code.indexOf("ROOM_") === 0     // caisse resto : room prise/jeton invalide/expiré
    || code.indexOf("SCREEN_") === 0   // écran de production : jeton révoqué/expiré/inconnu
    || code === "Invalid namespace";

export class ScangoSocket {
    constructor() {
        this.socket = null;
    }

    /**
     * @param {{room: string, token: string, role: 'caisse'|'phone'|'serveur',
     *          deviceId?: string, deviceNom?: string}} auth
     *        deviceId/deviceNom : métadonnées d'appareil (serveur resto), purement
     *        informatives - alimentent la présence de l'onglet Paramètres.
     * @param {{onConnect?: Function, onRefus?: (code: string) => void, onCoupure?: Function}} cb
     */
    connecter(auth, cb = {}) {
        this.deconnecter();

        const cible = construireCible();
        this.socket = io(cible.url, {
            path: cible.path,
            transports: ["websocket"],
            /* Les identifiants voyagent dans le handshake, pas dans l'URL : ils ne
               finissent ni dans les logs du proxy ni dans l'historique. */
            auth: {
                room: auth.room, token: auth.token, role: auth.role,
                deviceId: auth.deviceId || "", deviceNom: auth.deviceNom || "",
            },

            /* Ce socket vit dans la main d'un caissier qui marche dans un magasin :
               les micro-coupures wifi/4G sont la norme, pas l'exception. 5 tentatives
               (~5 s) seraient épuisées par le moindre passage derrière un rayon, et
               le téléphone abandonnerait DÉFINITIVEMENT - écran figé sur « Caisse
               injoignable » jusqu'à ce que quelqu'un recharge la page.
               Indissociable de l'arrêt franc sur refus définitif (voir plus bas) :
               sans lui, l'infini deviendrait du martèlement de serveur. */
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 10000,
            randomizationFactor: 0.5,

            withCredentials: false,
        });

        this.socket.on("connect", () => { if (cb.onConnect) cb.onConnect(); });

        /* Distinguer le refus du serveur (appairage invalide/expiré/room pleine)
           d'une simple coupure réseau : dans un cas il faut rappairer, dans l'autre
           il faut juste attendre. Sans ça le caissier ne saurait pas quoi faire. */
        this.socket.on("connect_error", (e) => {
            const code = e && e.message ? String(e.message) : "";

            if (estRefusDefinitif(code)) {
                /* socket.io réessaie sur connect_error QUOI QU'IL ARRIVE. Avec
                   reconnectionAttempts: Infinity, un lien expiré martèlerait le
                   serveur sans fin. Un refus ne se répare pas tout seul : on coupe. */
                try { this.socket.io.reconnection(false); } catch (err) { /* ignore */ }
                try { this.socket.disconnect(); } catch (err) { /* ignore */ }
                if (cb.onRefus) cb.onRefus(code);
                return;
            }

            if (cb.onCoupure) cb.onCoupure();
        });

        this.socket.on("disconnect", () => { if (cb.onCoupure) cb.onCoupure(); });

        return this.socket;
    }

    sur(event, cb) {
        if (this.socket) this.socket.on(event, cb);
    }

    /**
     * @returns {boolean} false si rien n'est parti - l'appelant DOIT le dire à
     * l'utilisateur plutôt que d'afficher un accusé mensonger.
     *
     * On exige `connected` au lieu de laisser socket.io mettre en tampon (son
     * comportement par défaut) : un scan livré trente secondes plus tard, une fois
     * le client parti, injecterait son panier dans le ticket du client SUIVANT.
     * Ici, perdre le message et le dire vaut mieux que le livrer en retard.
     */
    emettre(event, data) {
        if (!this.socket || !this.socket.connected) return false;
        this.socket.emit(event, data);
        return true;
    }

    connecte() {
        return !!(this.socket && this.socket.connected);
    }

    /** Idempotent : appelable depuis close() et beforeUnmount() sans précaution. */
    deconnecter() {
        if (!this.socket) return;
        try {
            this.socket.removeAllListeners();
            this.socket.disconnect();
        } catch (e) { /* ignore */ }
        this.socket = null;
    }
}
